import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { Search, Car, Plus, CheckCircle, XCircle, Download, Clock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import { useToast } from '../contexts/ToastContext';
import ActionMenu from '../components/ActionMenu';
import AddVehicleModal from '../components/AddVehicleModal';

const statusConfig = {
  assigned: { label: 'Assigned', color: 'success', icon: CheckCircle },
  unassigned: { label: 'Unassigned', color: 'warning', icon: XCircle },
  active: { label: 'Active', color: 'success', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'danger', icon: XCircle },
  pending: { label: 'Pending approval', color: 'warning', icon: Clock },
  approved: { label: 'Approved', color: 'success', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'danger', icon: XCircle },
  deactivated: { label: 'Deactivated', color: 'danger', icon: XCircle },
};

const operationalConfig = {
  active: { label: 'Active', color: 'success', icon: CheckCircle },
  deactivated: { label: 'Deactivated', color: 'danger', icon: XCircle },
};

const approvalConfig = {
  pending: { label: 'Pending', color: 'warning', icon: Clock },
  approved: { label: 'Approved', color: 'success', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'danger', icon: XCircle },
};

export default function Vehicles() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [assignModalVehicle, setAssignModalVehicle] = useState(null);
  const [assignDriverId, setAssignDriverId] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['fleet-vehicles', statusFilter, page, search],
    queryFn: () => {
      return fleetService.getVehicles({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        limit: 10,
        search: search || undefined,
      }).then((res) => res.data);
    },
  });

  const { data: driversListRes } = useQuery({
    queryKey: ['fleet-drivers-for-assign-table'],
    queryFn: () =>
      fleetService.getDrivers({ status: 'approved', limit: 200, page: 1 }).then((res) => res.data),
    enabled: !!assignModalVehicle,
  });

  const assignableDrivers = (driversListRes?.data ?? []).filter(
    (d) => d.approval_status === 'approved' && d.status === 'active'
  );

  const assignMutation = useMutation({
    mutationFn: ({ vehicleId, driverId }) => fleetService.assignVehicleToDriver(vehicleId, driverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers-for-assign'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicle'] });
      showSuccess('Vehicle assigned to driver.');
      setAssignModalVehicle(null);
      setAssignDriverId('');
    },
    onError: (err) =>
      showError(err?.response?.data?.message || err.message || 'Could not assign driver'),
  });

  useEffect(() => {
    if (assignModalVehicle?.driver_id) setAssignDriverId(assignModalVehicle.driver_id);
    else setAssignDriverId('');
  }, [assignModalVehicle]);

  const vehicles = data?.data || [];
  const pagination = data?.pagination || {};

  const handleView = (vehicle) => {
    navigate(`/vehicles/${vehicle.id}`);
  };

  const openAssignModal = (vehicle) => {
    setAssignModalVehicle(vehicle);
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">Vehicles</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark">
            Manage your fleet vehicles
          </p>
        </div>
        <div className="flex gap-2">
          <button className="hidden sm:flex items-center gap-2 px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Vehicle</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark" />
              <input
                type="text"
                placeholder="Search by manufacturer, plate, or transport licence..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                'all',
                'pending',
                'approved',
                'rejected',
                'assigned',
                'unassigned',
                'active',
                'inactive',
                'deactivated',
              ].map((status) => {
                const config =
                  status === 'all'
                    ? { label: 'All', color: 'primary' }
                    : statusConfig[status] || { label: status, color: 'primary' };
                return (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setPage(1);
                    }}
                    className={cn(
                      'px-4 py-2 rounded-xl font-medium transition-all text-sm',
                      statusFilter === status
                        ? config.color === 'success'
                          ? 'bg-success text-white shadow-lg'
                          : config.color === 'warning'
                          ? 'bg-warning text-white shadow-lg'
                          : config.color === 'danger'
                          ? 'bg-danger text-white shadow-lg'
                          : 'bg-primary text-white shadow-lg'
                        : 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark hover:scale-105'
                    )}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <div className="text-text-secondary dark:text-text-secondary-dark">Loading vehicles...</div>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="p-12 text-center">
            <Car className="w-16 h-16 text-text-tertiary dark:text-text-tertiary-dark mx-auto mb-4" />
            <div className="text-text-secondary dark:text-text-secondary-dark mb-2">No vehicles found</div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="text-primary hover:text-primary-dark font-medium"
            >
              Add your first vehicle
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-secondary dark:bg-surface-secondary-dark">
                  <tr>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Licence plate
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden md:table-cell">
                      Type
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Approval
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Assignment
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Operational
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden lg:table-cell">
                      Assigned driver
                    </th>
                    <th className="px-4 lg:px-6 py-4 text-right text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {vehicles.map((vehicle) => {
                    const isDeactivated = vehicle.status === 'deactivated';
                    const assignmentKey = vehicle.driver_id ? 'assigned' : 'unassigned';
                    const assignment = statusConfig[assignmentKey] || statusConfig.unassigned;
                    const AssignmentIcon = assignment.icon;
                    const opKey = isDeactivated ? 'deactivated' : 'active';
                    const operational = operationalConfig[opKey] || operationalConfig.active;
                    const OpIcon = operational.icon;
                    const appr = approvalConfig[vehicle.approval_status] || approvalConfig.pending;
                    const ApprIcon = appr.icon;
                    
                    return (
                      <tr
                        key={vehicle.id}
                        className="hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark cursor-pointer transition-colors"
                        onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                      >
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-info to-info-dark rounded-xl flex items-center justify-center shadow-lg">
                              <Car className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-text dark:text-text-dark">
                                {vehicle.model && vehicle.model !== '—' ? `${vehicle.make} ${vehicle.model}` : vehicle.make}{' '}
                                ({vehicle.year})
                              </div>
                              <div className="text-xs text-text-secondary dark:text-text-secondary-dark">
                                {vehicle.color} • {vehicle.license_plate || vehicle.licensePlate}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono font-semibold text-text dark:text-text-dark bg-surface-secondary dark:bg-surface-secondary-dark px-2 py-1 rounded">
                            {vehicle.license_plate || vehicle.licensePlate}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark hidden md:table-cell">
                          <span className="capitalize">{vehicle.vehicle_type || vehicle.vehicleType || 'Standard'}</span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
                              appr.color === 'success'
                                ? 'bg-success-light dark:bg-success-dark text-success'
                                : appr.color === 'warning'
                                ? 'bg-warning-light dark:bg-warning-dark text-warning'
                                : 'bg-danger-light dark:bg-danger-dark text-danger'
                            )}
                          >
                            <ApprIcon className="w-3.5 h-3.5" />
                            {appr.label}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
                              assignment.color === 'success'
                                ? 'bg-success-light dark:bg-success-dark text-success'
                                : assignment.color === 'warning'
                                ? 'bg-warning-light dark:bg-warning-dark text-warning'
                                : 'bg-danger-light dark:bg-danger-dark text-danger'
                            )}
                          >
                            <AssignmentIcon className="w-3.5 h-3.5" />
                            {assignment.label}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
                              operational.color === 'success'
                                ? 'bg-success-light dark:bg-success-dark text-success'
                                : 'bg-danger-light dark:bg-danger-dark text-danger'
                            )}
                          >
                            <OpIcon className="w-3.5 h-3.5" />
                            {operational.label}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-secondary-dark hidden lg:table-cell">
                          {vehicle.driver ? (
                            <span className="font-medium">{vehicle.driver.first_name} {vehicle.driver.last_name}</span>
                          ) : (
                            <span className="text-text-tertiary dark:text-text-tertiary-dark">Unassigned</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                          <div
                            className="flex items-center justify-end gap-2 relative z-10 flex-wrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ActionMenu
                              onView={() => handleView(vehicle)}
                              onAssign={
                                vehicle.approval_status === 'approved' && !isDeactivated
                                  ? () => openAssignModal(vehicle)
                                  : undefined
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total > 0 && (
              <div className="p-4 lg:p-6 border-t border-border dark:border-border-dark flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface-secondary/30 dark:bg-surface-secondary-dark/30">
                <div className="text-sm text-text-secondary dark:text-text-secondary-dark">
                  Showing <span className="font-semibold text-text dark:text-text-dark">{vehicles.length}</span> of{' '}
                  <span className="font-semibold text-text dark:text-text-dark">{pagination.total || 0}</span> vehicles
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors text-sm font-medium"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.pages || page >= pagination.pages}
                    className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors text-sm font-medium shadow-lg"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Vehicle Modal */}
      <AddVehicleModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} />

      {assignModalVehicle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vehicles-assign-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setAssignModalVehicle(null);
              setAssignDriverId('');
            }
          }}
        >
          <div
            className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 id="vehicles-assign-title" className="text-lg font-bold text-text dark:text-text-dark pr-2">
                Assign driver
              </h2>
              <button
                type="button"
                onClick={() => {
                  setAssignModalVehicle(null);
                  setAssignDriverId('');
                }}
                className="p-1 rounded-lg hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark text-text-secondary shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-2">
              {assignModalVehicle.model && assignModalVehicle.model !== '—'
                ? `${assignModalVehicle.make} ${assignModalVehicle.model}`
                : assignModalVehicle.make}{' '}
              <span className="font-mono">
                ({assignModalVehicle.license_plate || assignModalVehicle.licensePlate})
              </span>
            </p>
            <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-4">
              Only approved, active drivers are listed. Assigning replaces this driver&apos;s previous vehicle if they
              had one.
            </p>
            <label htmlFor="vehicles-assign-driver" className="block text-sm font-medium text-text dark:text-text-dark mb-2">
              Driver
            </label>
            <select
              id="vehicles-assign-driver"
              value={assignDriverId}
              onChange={(e) => setAssignDriverId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text dark:text-text-dark mb-4"
            >
              <option value="">Select a driver…</option>
              {assignableDrivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.first_name} {d.last_name} — {d.email}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAssignModalVehicle(null);
                  setAssignDriverId('');
                }}
                className="px-4 py-2 rounded-lg border border-border dark:border-border-dark text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!assignDriverId || assignMutation.isPending}
                onClick={() =>
                  assignDriverId &&
                  assignMutation.mutate({ vehicleId: assignModalVehicle.id, driverId: assignDriverId })
                }
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
              >
                {assignMutation.isPending ? 'Saving…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
